import React from "react";
import { Link } from "react-router-dom";
import { Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

export const SIDEBAR_NAV_ROW_HEIGHT = 30;
export const SIDEBAR_NAV_NESTED_ROW_HEIGHT = 28;
export const SIDEBAR_NAV_ITEM_PADDING_X = 8;
export const SIDEBAR_NAV_ICON_SIZE = 16;
export const SIDEBAR_NAV_ICON_GAP = 8;
/** Align nested item text with parent item text (after icon + gap). */
export const SIDEBAR_NAV_NESTED_PADDING_LEFT =
  SIDEBAR_NAV_ITEM_PADDING_X + SIDEBAR_NAV_ICON_SIZE + SIDEBAR_NAV_ICON_GAP;

export const SidebarNavPanel = styled(Box)(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  flexDirection: "column",
  minHeight: 0,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  overflow: "hidden",
}));

export const SidebarNavScrollArea = styled(Box)(({ theme }) => ({
  display: "flex",
  flex: 1,
  minHeight: 0,
  flexDirection: "column",
  overflowX: "hidden",
  overflowY: "auto",
  paddingTop: 10,
  paddingBottom: 8,
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": {
    width: 6,
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 999,
    backgroundColor: alpha(theme.palette.text.primary, 0.18),
  },
}));

export const SidebarNavSection = styled(Box)({
  display: "flex",
  flexShrink: 0,
  flexDirection: "column",
  gap: 2,
  padding: "0 8px",
});

export const SidebarNavGroup = styled(Box)({
  display: "flex",
  flexShrink: 0,
  flexDirection: "column",
  paddingBottom: 8,
});

export const SidebarNavSectionHeader = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})(({ theme, isOpen = true }) => ({
  display: "flex",
  height: 18,
  flexShrink: 0,
  alignItems: "center",
  padding: "0 16px",
  marginBottom: 2,
  color: theme.palette.text.disabled,
  fontSize: 11,
  fontWeight: 500,
  lineHeight: 1,
  opacity: isOpen ? 1 : 0,
  transition: "opacity 150ms ease",
}));

export const SidebarNavItemRoot = styled(ButtonBase, {
  shouldForwardProp: (prop) =>
    !["selected", "isOpen", "nested", "danger"].includes(prop),
})(({ theme, selected, isOpen, nested, danger }) => ({
  display: "flex",
  width: isOpen ? "calc(100% - 4px)" : 32,
  height: nested ? SIDEBAR_NAV_NESTED_ROW_HEIGHT : SIDEBAR_NAV_ROW_HEIGHT,
  minHeight: nested ? SIDEBAR_NAV_NESTED_ROW_HEIGHT : SIDEBAR_NAV_ROW_HEIGHT,
  alignItems: "center",
  justifyContent: isOpen ? "flex-start" : "center",
  gap: SIDEBAR_NAV_ICON_GAP,
  margin: "0 2px",
  borderRadius: 8,
  padding: nested
    ? `0 ${SIDEBAR_NAV_ITEM_PADDING_X}px 0 ${SIDEBAR_NAV_NESTED_PADDING_LEFT}px`
    : isOpen
    ? `0 ${SIDEBAR_NAV_ITEM_PADDING_X}px`
    : 0,
  boxSizing: "border-box",
  color: danger
    ? selected
      ? theme.palette.error.main
      : theme.palette.error.light
    : selected
    ? theme.palette.text.primary
    : theme.palette.text.secondary,
  backgroundColor: selected ? theme.palette.action.selected : "transparent",
  textDecoration: "none",
  overflow: "hidden",
  transition: "background-color 150ms ease, color 150ms ease, width 200ms ease",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: danger ? theme.palette.error.main : theme.palette.text.primary,
  },
  "& .sidebar-nav-icon": {
    display: "flex",
    width: SIDEBAR_NAV_ICON_SIZE,
    height: SIDEBAR_NAV_ICON_SIZE,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    color: selected ? theme.palette.text.primary : theme.palette.text.disabled,
  },
  "& .MuiSvgIcon-root": {
    width: SIDEBAR_NAV_ICON_SIZE,
    height: SIDEBAR_NAV_ICON_SIZE,
    fontSize: SIDEBAR_NAV_ICON_SIZE,
  },
}));

export const SidebarNavItemLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "nested" && prop !== "danger",
})(({ theme, nested, danger }) => ({
  minWidth: 0,
  overflow: "hidden",
  color: danger ? "inherit" : theme.palette.text.primary,
  fontSize: nested ? 13 : 14,
  fontWeight: nested ? 400 : 400,
  lineHeight: "18px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export function SidebarNavItem({
  item,
  isOpen = true,
  selected = false,
  onClick,
  nested = false,
  danger = false,
  id,
}) {
  const label = item?.label;
  const icon = item?.icon;
  const tooltipTitle = isOpen ? "" : item?.tooltip || label;
  const rootProps = item?.path
    ? { component: Link, to: item.path }
    : { component: "button", type: "button" };

  return (
    <Tooltip placement="right" title={tooltipTitle}>
      <SidebarNavItemRoot
        {...rootProps}
        id={id}
        data-item-id={label}
        isOpen={isOpen}
        nested={nested}
        danger={danger}
        selected={selected}
        onClick={onClick ?? item?.onClick}
        aria-current={selected ? "page" : undefined}
        aria-label={label}
      >
        {!nested && icon ? (
          <Box className="sidebar-nav-icon">{icon}</Box>
        ) : null}
        {isOpen ? <SidebarNavItemLabel nested={nested}>{label}</SidebarNavItemLabel> : null}
      </SidebarNavItemRoot>
    </Tooltip>
  );
}
